/**
 * Branch Service
 * Handles all branch-related API calls
 * @module services/branches/branchService
 */

import httpClient from '@/utils/httpClient';
import { isValidId, createInvalidIdResponse } from '@/services/shared/validationHelpers';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';

const branchService = {
  /**
   * Get all branches
   * @returns {Promise<Object>} - Response data
   */
  getAll: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.BRANCHES.BASE));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch by ID
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} - Response data
   */
  getById: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.BRANCHES.BY_ID, id));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Create a new branch
   * @param {Object} branchData - Branch data
   * @returns {Promise<Object>} - Response data
   */
  create: async (branchData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.BRANCHES.CREATE), branchData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update a branch using PATCH
   * @param {string} id - Branch ID
   * @param {Object} branchData - Branch data to update
   * @param {string} [branchData.name] - Branch name
   * @param {string} [branchData.address] - Branch address
   * @param {string} [branchData.phone] - Branch phone
   * @param {string} [branchData.email] - Branch email
   * @param {string} [branchData.managerName] - Manager name
   * @param {string} [branchData.status] - Branch status (e.g., 'active', 'inactive')
   * @param {string} [branchData.description] - Branch description
   * @returns {Promise<Object>} - Response data
   */
  update: async (id, branchData) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.BRANCHES.PATCH, id), branchData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Delete a branch
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} - Response data
   */
  delete: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.BRANCHES.DELETE, id));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch timezone
   * @param {string} id - Branch ID
   * @param {string} timezone - Timezone (e.g., "Asia/Dubai")
   * @returns {Promise<Object>} - Response data
   */
  updateTimezone: async (id, timezone) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.SETTINGS, id),
        { timezone }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch work hours
   * @param {string} id - Branch ID
   * @param {Object} workHours - Work hours object
   * @param {string} workHours.workStart - Work start time (e.g., "10:00")
   * @param {string} workHours.workEnd - Work end time (e.g., "18:00")
   * @param {string[]} workHours.workDays - Array of work days (e.g., ["Monday", "Tuesday"])
   * @param {number} [workHours.gracePeriod] - Grace period in minutes (optional)
   * @returns {Promise<Object>} - Response data
   */
  updateWorkHours: async (id, workHours) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.SETTINGS, id),
        { workHours }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch timezone and work hours together
   * @param {string} id - Branch ID
   * @param {string} timezone - Timezone (e.g., "Asia/Dubai")
   * @param {Object} workHours - Work hours object
   * @param {string} workHours.workStart - Work start time (e.g., "10:00")
   * @param {string} workHours.workEnd - Work end time (e.g., "18:00")
   * @param {string[]} workHours.workDays - Array of work days (e.g., ["Monday", "Tuesday"])
   * @param {number} [workHours.gracePeriod] - Grace period in minutes (optional)
   * @returns {Promise<Object>} - Response data
   */
  updateSettings: async (id, { timezone, workHours, scheduledRemote, useOrgShifts }) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const payload = {};
      // null clears branch override → organization settings
      if (timezone !== undefined) payload.timezone = timezone;
      if (workHours !== undefined) payload.workHours = workHours;
      if (scheduledRemote !== undefined) payload.scheduledRemote = scheduledRemote;
      if (useOrgShifts !== undefined) payload.useOrgShifts = useOrgShifts;
      
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.SETTINGS, id),
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch settings
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} - Response data containing branch settings
   * @example
   * {
   *   "success": true,
   *   "message": "تم جلب إعدادات الفرع بنجاح",
   *   "data": {
   *     "branchId": "...",
   *     "branchName": "...",
   *     "timezone": "...",
   *     "workStart": "...",
   *     "workEnd": "...",
   *     "workDays": [...],
   *     "gracePeriod": 30,
   *     "branchSettings": {...}
   *   }
   * }
   */
  getSettings: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.BRANCHES.GET_SETTINGS, id));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch late penalty settings (resolved + source: branch | organization)
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} - Response data
   */
  getLatePenaltySettings: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.BRANCHES.LATE_PENALTY_SETTINGS, id)
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch late penalty settings
   * @param {string} id - Branch ID
   * @param {Object} latePenaltySettings - Late penalty settings object
   * @param {boolean} latePenaltySettings.enabled - Whether late penalty is enabled
   * @param {Array} latePenaltySettings.rules - Array of penalty rules
   * @param {number} rules[].delayMinutes - Delay in minutes
   * @param {string|null} rules[].penaltyType - Penalty type ('quarter_day', 'half_day', 'full_day', 'custom', or null)
   * @param {number|null} rules[].penaltyAmount - Penalty amount (for custom type)
   * @param {number|null} rules[].penaltyDays - Penalty days (for custom type)
   * @returns {Promise<Object>} - Response data
   */
  updateLatePenaltySettings: async (id, latePenaltySettings) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      // Pass through null / followCompany to clear branch override → company settings
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.LATE_PENALTY_SETTINGS, id),
        latePenaltySettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch auto-checkout settings (resolved + source: branch | organization)
   * @param {string} id - Branch ID
   * @returns {Promise<Object>} - Response data
   */
  getAutoCheckoutSettings: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.BRANCHES.AUTO_CHECKOUT_SETTINGS, id)
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch auto-checkout settings
   * @param {string} id - Branch ID
   * @param {Object} autoCheckoutSettings - Settings object, or { followCompany: true } to clear override
   * @returns {Promise<Object>} - Response data
   */
  updateAutoCheckoutSettings: async (id, autoCheckoutSettings) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.AUTO_CHECKOUT_SETTINGS, id),
        autoCheckoutSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch auto-checkout deduction settings (resolved + source: branch | organization)
   * @param {string} id - Branch ID
   * @returns {Promise<Object>}
   */
  getAutoCheckoutDeductionSettings: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.BRANCHES.AUTO_CHECKOUT_DEDUCTION_SETTINGS, id)
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch auto-checkout deduction settings
   * @param {string} id - Branch ID
   * @param {Object} autoCheckoutDeductionSettings - { policy, fixedAmount } or { followCompany: true }
   * @returns {Promise<Object>}
   */
  updateAutoCheckoutDeductionSettings: async (id, autoCheckoutDeductionSettings) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.AUTO_CHECKOUT_DEDUCTION_SETTINGS, id),
        autoCheckoutDeductionSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get branch check-in selfie settings (resolved + source: branch | organization)
   * @param {string} id - Branch ID
   * @returns {Promise<Object>}
   */
  getCheckInSelfieSettings: async (id) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.BRANCHES.CHECK_IN_SELFIE_SETTINGS, id)
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update branch check-in selfie settings
   * @param {string} id - Branch ID
   * @param {Object} checkInSelfieSettings - Settings, or { followCompany: true } to inherit org
   * @returns {Promise<Object>}
   */
  updateCheckInSelfieSettings: async (id, checkInSelfieSettings) => {
    if (!isValidId(id, 'Branch')) {
      return createInvalidIdResponse('Branch');
    }
    try {
      const response = await httpClient.patch(
        getEndpointPath(ENDPOINTS.BRANCHES.CHECK_IN_SELFIE_SETTINGS, id),
        checkInSelfieSettings
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default branchService;

